<script lang="ts">
  /* ONE SURFACE FOR EVERYTHING THE CHART HAS TO SAY ON A PHONE.
   *
   * Ported from `apps/web/src/MobileSheet.svelte` with its logic intact. Almost
   * every constant and guard below was arrived at by measuring a real device or
   * by shipping the bug first, and none of it is worth re-deriving:
   *
   *   - the two detents, and why there is no third
   *   - the ceiling being the CONTENT height, not the detent
   *   - measuring a hugging wrapper rather than a scroll box
   *   - 12 px of drag slop, because a boat moves
   *   - `setPointerCapture` in a try, because it throws
   *   - resting on the dock, which is a licensing requirement
   *   - landscape being a column, not a short sheet
   *
   * ═══ WHAT CHANGED IN THE MOVE ═══
   *
   * The head and the body belong to `<Panel>` now, so this owns the grabber,
   * the height and the gesture and nothing else. It learns how tall its content
   * wants to be through the `SHEET_MEASURE` context rather than by measuring a
   * child it happens to own — see measure.ts.
   *
   * ═══ WHY A SHEET AND NOT FOUR PANELS ═══
   *
   * This app grew four panels with four geometries — layers, route, object
   * inspect, tile cache — each designed as a desktop overlay and each becoming
   * a full-screen takeover on a small screen. Measured on an iPhone 14 Pro: the
   * route panel with currents on covered 492 px of 660, three quarters of the
   * screen, over a chart that is the entire point of the page.
   *
   * A chart plotter does not work that way. The chart is the interface and
   * detail is something you pull up over it and push back down, which is one
   * surface with a few resting positions rather than four fixed rectangles.
   */
  import type { Snippet } from 'svelte';
  import { setContext } from 'svelte';
  import { SHEET_MEASURE, type ReportHeight } from './measure';
  import { layout } from './layout.svelte';
  import type { Detent } from './panels.svelte';

  let {
    detent = $bindable<Detent>('peek'),
    /** For screen readers, and for the close button's label. */
    label,
    onclose,
    children
  }: {
    detent?: Detent;
    label: string;
    onclose?: () => void;
    children: Snippet;
  } = $props();

  /** One line and a grabber. Absolute rather than proportional: it does not
   *  want to grow on a tablet. */
  const PEEK_PX = 96;
  /** The ceiling on OPEN, as a fraction of the room above the dock. Well short
   *  of full screen on purpose: a sheet that fills the window stops looking
   *  like a sheet, and the reader loses the fact that the chart is behind it. */
  const OPEN = 0.7;
  /** The drag surface's own height. Material asks for a 48 dp hit target behind
   *  a grab handle; the visible pill stays 40x4. */
  const GRAB_PX = 44;

  /** Live height while a finger is down; null when resting on a detent. */
  let dragH = $state<number | null>(null);

  /** What the panel inside says it needs. Reported through context. */
  let contentH = $state(PEEK_PX);
  setContext<ReportHeight>(SHEET_MEASURE, (px) => (contentH = px));

  /** The screen MINUS the dock, which is the only room this may use. Read from
   *  the same custom property the CSS uses, so the two cannot disagree about
   *  where the bottom is. */
  const avail = () => {
    if (typeof window === 'undefined') return 800;
    const dock =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--gd-dock-h')) || 0;
    return Math.max(200, window.innerHeight - dock);
  };

  /** What the sheet would need to show everything. */
  const natural = $derived(GRAB_PX + contentH);

  /* A detent is a CEILING, not a target: the sheet rises to the smaller of the
   * detent and what it is actually holding. Without this, opening the route
   * sheet put 268 px of empty panel over the chart. */
  const heightFor = (d: Detent): number =>
    d === 'peek' ? PEEK_PX : Math.max(PEEK_PX, Math.min(Math.round(avail() * OPEN), natural));

  const height = $derived(dragH ?? heightFor(detent));

  /* ═══ THE GESTURE ═══
   *
   * Pointer events, not touch: one code path covers a finger, a trackpad and a
   * stylus, and `setPointerCapture` keeps the drag alive when the finger leaves
   * the grabber — which it always does, because the grabber moves with it.
   *
   * Dragged by HEIGHT rather than by a transform. A transform is cheaper to
   * animate but leaves the content laid out at its resting size, so a scroll
   * region inside it is the wrong length for the whole drag. Height is what the
   * content actually needs to know. */
  let sheetEl = $state<HTMLElement | null>(null);
  let startY = 0;
  let startH = 0;
  let dragging = $state(false);

  /* ═══ DID THE FINGER ACTUALLY MOVE ═══
   *
   * `pointerup` on the element that took `pointerdown` is followed by a
   * `click`, and `click` is what toggles the sheet — so a completed DRAG would
   * be followed by a tap that put it straight back. Whether the browser
   * suppresses that click after a touch drag is a per-engine slop rule this
   * code does not get to depend on, and the consequence is a sheet that shuts
   * the instant you finish dragging it open.
   *
   * 12 px because a boat moves: under motion people press harder and slide,
   * which turns intended taps into small drags. Tight enough to catch a
   * deliberate drag, loose enough to forgive a wobble. */
  const MOVED_PX = 12;
  let moved = false;

  function down(e: PointerEvent) {
    /* Guarded because it THROWS for a pointer the browser no longer considers
     * active — "No active pointer with the given id is found" — and an
     * exception here would leave `dragging` false with a finger on the screen,
     * so the sheet simply would not move. Without capture the drag still works
     * while the pointer stays over the grabber, which beats not starting. */
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* see above */
    }
    startY = e.clientY;
    startH = heightFor(detent);
    dragH = startH;
    dragging = true;
    moved = false;
  }

  function move(e: PointerEvent) {
    if (!dragging) return;
    if (Math.abs(e.clientY - startY) > MOVED_PX) moved = true;
    /* Up is negative in client coordinates, and up makes the sheet taller. */
    dragH = Math.min(heightFor('open'), Math.max(48, startH - (e.clientY - startY)));
  }

  function up() {
    if (!dragging) return;
    dragging = false;
    const h = dragH ?? heightFor(detent);
    dragH = null;

    /* Dragged well below peek: that is a dismissal, and pretending it was a
     * snap back to peek makes the sheet feel like it is fighting you. */
    if (h < PEEK_PX * 0.6) {
      onclose?.();
      detent = 'peek';
      return;
    }
    /* Nearest detent by height. Nearest rather than direction-of-travel, so a
     * small accidental drag returns where it started instead of committing to a
     * move nobody asked for. */
    detent = Math.abs(heightFor('peek') - h) <= Math.abs(heightFor('open') - h) ? 'peek' : 'open';
  }

  /** Tap the grabber to open, tap again to put it away. With two detents this
   *  is a toggle, which is the thing a grab handle most obviously is. */
  function tapGrab() {
    if (dragging) return;
    /* A drag already decided where this rests. CONSUMED rather than merely
     * read, so a genuine tap straight after a drag still works. */
    if (moved) {
      moved = false;
      return;
    }
    detent = detent === 'peek' ? 'open' : 'peek';
  }

  /* ═══ ITS WIDTH, IN LANDSCAPE, AND NOTHING ELSE ═══
   *
   * This used to publish its HEIGHT, sixty times a second during a drag, plus a
   * `data-sheet` attribute naming its detent, so five separately-anchored
   * floating controls could translate out of the way and then fade out. There
   * are no floating controls now: the dock is a fixed object and the sheet
   * stops on top of it, so neither the height nor the detent is anybody else's
   * business.
   *
   * The width still is. In landscape the sheet is a column down the right edge
   * and the top rail has to stop short of it — the ONE measurement still
   * crossing a component boundary in the phone layout, down from fourteen, and
   * published by the element it describes rather than guessed at by the one
   * reading it. */
  $effect(() => {
    const root = document.documentElement;
    if (!layout.landscape) {
      root.style.removeProperty('--gd-sheet-w');
      return;
    }
    root.style.setProperty('--gd-sheet-w', `${sheetEl?.offsetWidth ?? 0}px`);
    return () => root.style.removeProperty('--gd-sheet-w');
  });
</script>

<section
  class="msheet"
  class:dragging
  class:landscape={layout.landscape}
  bind:this={sheetEl}
  style="height: {height}px"
  aria-label={label}
>
  <!-- The grabber is the control AND the drag surface. `touch-action: none` on
       it is the load-bearing line: without it the browser claims the drag as a
       scroll and the sheet never moves. -->
  <div
    class="grab"
    role="button"
    tabindex="0"
    aria-label="{label} — drag or tap to resize"
    aria-expanded={detent === 'open'}
    onpointerdown={down}
    onpointermove={move}
    onpointerup={up}
    onpointercancel={up}
    onclick={tapGrab}
    onkeydown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        tapGrab();
      }
      if (e.key === 'Escape') onclose?.();
    }}
  >
    <span class="pill"></span>
  </div>

  <!-- Clipped rather than unmounted OR `display: none`. Unmounting loses scroll
       position and focus, so collapsing to peek and pulling straight back up
       would dump you at the top of a schedule you were reading. `display: none`
       keeps those but makes the content unmeasurable, and the sheet has to know
       how tall its content is BEFORE it opens. -->
  <div class="hold" class:clipped={detent === 'peek' && !layout.landscape}>
    {@render children()}
  </div>
</section>

<style>
  .msheet {
    position: absolute;
    left: 0;
    right: 0;
    /* It stops on top of the dock, and that is a legal requirement expressed as
       one number: the dock's last line is the OGL-Canada acknowledgement, which
       is the condition the public ENC proxy is licensed under. Measured by the
       element that owns it. */
    bottom: 0;
    z-index: 7;
    display: flex;
    flex-direction: column;
    background: var(--s-1);
    border-top: 1px solid var(--line);
    border-radius: var(--r-4) var(--r-4) 0 0;
    box-shadow: var(--el-3);
    /* The whole sheet, so a fling does not rubber-band the chart behind it. */
    overscroll-behavior: contain;
    overflow: hidden;
    transition: height var(--dur-base) var(--ease-sheet);
  }
  /* No transition while a finger is on it: the height IS the finger position,
     and easing toward it makes the sheet lag behind the hand. */
  .msheet.dragging {
    transition: none;
  }
  @media (prefers-reduced-motion: reduce) {
    .msheet {
      transition: none;
    }
  }

  .grab {
    height: 44px;
    display: grid;
    place-items: center;
    flex: none;
    cursor: grab;
    touch-action: none;
    -webkit-user-select: none;
    user-select: none;
  }
  .msheet.dragging .grab {
    cursor: grabbing;
  }
  /* NOT `.bar`. chart-ui's layer-toggle strip is `.bar`, and this pill lives
     inside the sheet — so any overlap check using a bare `.bar` reported the
     sheet overlapping its own handle forever, and `_mobile-proof.mjs` grew a
     four-line comment about the workaround. Cheaper to not collide. */
  .pill {
    width: 40px;
    height: 4px;
    border-radius: var(--r-pill);
    background: var(--line);
  }
  .grab:focus-visible .pill {
    background: var(--cy);
    outline: 2px solid var(--cy);
    outline-offset: 4px;
  }

  .hold {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  /* At peek only the panel's own head is visible, and it is the peek line. */
  .hold.clipped {
    overflow: hidden;
  }

  /* ═══ LANDSCAPE IS NOT A SHORT PORTRAIT ═══
   *
   * A phone on its side is 852 x 393. A sheet rising from the bottom of a
   * 393 px screen leaves a letterbox: measured, the route sheet at peek plus
   * the existing chrome took 72% of it. The shape that fits is a side panel,
   * full height, because in landscape WIDTH is what you have.
   *
   * The detents stop applying — there is no useful middle between "a column of
   * detail" and "gone" — so the height is overridden and the drag simply does
   * not move it. Honest rather than broken: the grabber stays as the close
   * affordance and the keyboard path still works. */
  .msheet.landscape {
    left: auto;
    right: 0;
    top: 0;
    width: min(340px, 45vw);
    height: auto !important;
    border-radius: 0;
    border-top: 0;
    border-left: 1px solid var(--line);
    box-shadow: -8px 0 30px rgb(0 0 0 / 0.32);
    padding-top: env(safe-area-inset-top);
  }
  /* ═══ NO GRABBER IN LANDSCAPE ═══
   *
   * It was kept as "the close affordance", drawn as a vertical bar — and a
   * vertical bar reads as "this edge moves", which it does not: the detents do
   * not apply here and the drag is inert. So it was 44 px of a 255 px column
   * spent on a handle for something that cannot be handled.
   *
   * `_mobile-proof.mjs` measures the body and fails under 120 px; with the
   * grabber and a four-button foot it was 42. The close button in the panel's
   * own head is the affordance, it is a real 44 px target, and Escape still
   * closes. A side column is not a sheet and does not need a sheet's handle. */
  .msheet.landscape .grab {
    display: none;
  }
</style>
